import { ReactNode } from 'react';

import { getOrganizationLogo, ShortedBankName } from '@revenuemarket/bank';
import { CDS_ZINDEX, Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { ResponseStatus } from '@revenuemarket/http';
import { Toast, Typography } from '@revenuemarket/ui';
import { Button, Modal, Tag } from 'antd';
import Image from 'next/image';
import { usePurgeBankAccount } from '~hooks/queries/bank-accounts/query';
import { useCompanyId } from '~hooks/useCompanyId';
import { css } from '~libs/emotion';
import { AccountTypeTextMap } from '~models/bank/enum';
import { SimpleBankAccount } from '~models/cashFlow';

interface Props {
  title: ReactNode;
  description: ReactNode;
  actionSlot?: ReactNode;
  bankAccounts: SimpleBankAccount[];
}

const BankAccountScrapeErrorSummary = ({ bankAccounts, actionSlot, description, title }: Props) => {
  const { companyId } = useCompanyId();
  const { mutateAsync: purgeBankAccount } = usePurgeBankAccount();
  const [modal, contextHolder] = Modal.useModal();

  if (bankAccounts.length === 0) {
    return null;
  }

  return (
    <Flex direction="column" gap={8}>
      {contextHolder}
      <Typography variant="body1" weight={600} color={theme.colorTokens.textDefault}>
        {title}&nbsp;
        <Typography variant="body1" color={theme.colorTokens.textDescription}>
          {bankAccounts.length}
        </Typography>
      </Typography>
      <Typography variant="body1" color={theme.colorTokens.textDescription}>
        {description}
      </Typography>
      {actionSlot}
      <Flex direction="column">
        {bankAccounts.map((bankAccount) => (
          <BankAccountRow
            key={bankAccount.accountId}
            item={bankAccount}
            onPurge={() => {
              modal.confirm({
                title: `${ShortedBankName[bankAccount.organizationCode]} ${
                  bankAccount.displayAccountNumber ?? bankAccount.displayAccountNumber
                } 계좌 수집을 중단하시겠습니까?`,
                content:
                  '수집 중단한 계좌는 더이상 수집하지 않으며, 계좌 목록과 거래내역에 노출되지 않습니다. 단, 수집 중단 시점 이전의 계좌 목록 및 거래내역은 유지됩니다. 추후 수집을 재개하고 싶다면 계좌 순서/숨김 설정에서 수집을 재개해주세요.',
                onOk: async () => {
                  const response = await purgeBankAccount({
                    companyId,
                    bankAccountId: bankAccount.accountId,
                  });
                  if (response.Status === ResponseStatus.SUCCESS) {
                    Toast.success('계좌 수집이 중단되었습니다.');
                  } else {
                    Toast.error('계좌 수집 중단에 실패했습니다.');
                  }
                },
                zIndex: CDS_ZINDEX.POP_UP,
              });
            }}
          />
        ))}
      </Flex>
    </Flex>
  );
};

const BankAccountRow = ({ item, onPurge }: { item: SimpleBankAccount; onPurge: () => void }) => {
  return (
    <Flex
      justify="space-between"
      align="center"
      className={css`
        padding: 12px 0;
        border-bottom: 1px solid ${theme.colorTokens.borderDefault};
      `}
    >
      <Flex align="center" gap={12}>
        <Image
          width={16}
          height={16}
          src={getOrganizationLogo(item.organizationCode).x1}
          alt={item.organizationCode}
        />
        <Typography variant="body1" color={theme.colorTokens.textDefault}>
          {item.displayAccountNumber ?? item.accountNumber}
        </Typography>
        <Tag>{item.accountType === null ? '증권' : AccountTypeTextMap[item.accountType]}</Tag>
      </Flex>
      <Button danger size="small" type="text" onClick={() => onPurge()}>
        수집 중단
      </Button>
    </Flex>
  );
};

export default BankAccountScrapeErrorSummary;
